import React from "react";
import { GraduationCap, Heart, Sparkles, Feather, BookOpen, Cpu } from "lucide-react";

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="bg-plum-deepest section-pad relative overflow-hidden">
      <span aria-hidden className="hero-monogram" style={{ top: "5%", left: "-5%", color: "rgba(201,169,97,0.06)" }}>
        m
      </span>

      <div className="container-edit relative">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-2xl bg-gradient-to-br from-plum to-plum-deepest border border-gold/25 flex items-center justify-center relative overflow-hidden">
              <svg
                aria-hidden
                className="absolute inset-0 w-full h-full opacity-[0.13]"
                viewBox="0 0 400 500"
                preserveAspectRatio="xMidYMid slice"
              >
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="1" fill="#C9A961" />
                  </pattern>
                </defs>
                <rect width="400" height="500" fill="url(#grid)" />
                <g stroke="#C9A961" strokeWidth="0.7" fill="none" opacity="0.6">
                  <path d="M40 80 L160 80 L160 140" />
                  <path d="M240 60 L320 60 L320 200 L280 200" />
                  <path d="M60 380 L60 440 L200 440" />
                  <path d="M280 420 L340 420 L340 360" />
                </g>
                <g fill="#C9A961" opacity="0.5">
                  <circle cx="40" cy="80" r="2.5" />
                  <circle cx="160" cy="140" r="2.5" />
                  <circle cx="240" cy="60" r="2.5" />
                  <circle cx="280" cy="200" r="2.5" />
                  <circle cx="200" cy="440" r="2.5" />
                  <circle cx="340" cy="360" r="2.5" />
                </g>
              </svg>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(201,169,97,0.22),transparent_60%)]" />
              <Feather size={22} className="absolute top-6 left-6 text-gold/70" strokeWidth={1.4} />
              <Sparkles size={20} className="absolute top-6 right-6 text-gold/70" strokeWidth={1.4} />
              <BookOpen size={22} className="absolute bottom-6 left-6 text-gold/70" strokeWidth={1.4} />
              <Cpu size={20} className="absolute bottom-6 right-6 text-gold/70" strokeWidth={1.4} />
              <div className="absolute inset-4 rounded-xl border border-gold/15 pointer-events-none" />
              <div className="relative text-center px-6 z-10">
                <div className="font-display text-[10rem] text-gold/85 leading-none mb-2 font-display-italic drop-shadow-[0_4px_24px_rgba(201,169,97,0.25)]">
                  M
                </div>
                <div className="flex items-center justify-center gap-3 mb-2">
                  <span className="gold-rule" />
                  <span className="font-display text-2xl text-cream tracking-wide">Melanie</span>
                  <span className="gold-rule" />
                </div>
                <div className="eyebrow text-gold/75">Founder &middot; AI Builder &middot; PhD</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="eyebrow text-gold mb-5">About the Studio</div>
            <h2 data-testid="about-headline" className="h-section text-cream mb-8">
              A PhD's precision, an <span className="font-display-italic text-gold">AI builder's</span> obsession with what actually works.
            </h2>

            <div className="space-y-5 text-cream/85 font-body leading-relaxed text-lg">
              <p>
                I'm Melanie — a PhD-credentialed educator with over a decade of experience teaching
                others how to think, write, and communicate clearly. Today, I bring that same
                precision into building <span className="text-cream">AI chatbots</span> that actually understand your business.
              </p>
              <p>
                Melavate Studios exists for one reason: <span className="text-cream">most small business
                owners want AI but don't have time to figure it out.</span> They lose leads at 11pm.
                They miss bookings on weekends. They wonder if they're falling behind. I solve that
                — with a chatbot that's trained on YOUR business, embedded on YOUR site, and working
                while you live your life.
              </p>
              <p className="font-display-italic text-gold text-xl">
                "I treat every chatbot like it's the only one on my desk."
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-12">
              <Pillar
                icon={<GraduationCap size={20} />}
                title="PhD-Level"
                blurb="Academic precision built into every conversation flow."
              />
              <Pillar
                icon={<Sparkles size={20} />}
                title="AI-Fluent"
                blurb="Real builds — not generic GPT wrappers."
              />
              <Pillar
                icon={<Heart size={20} />}
                title="Personal"
                blurb="You work directly with me. Always."
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pillar({ icon, title, blurb }) {
  return (
    <div className="border-t border-gold/20 pt-5">
      <div className="text-gold mb-3">{icon}</div>
      <div className="font-display text-cream text-lg mb-1.5">{title}</div>
      <div className="text-cream/65 text-sm leading-relaxed">{blurb}</div>
    </div>
  );
}
